import {
  AttachmentDataProvider,
  CommentAnnotationDataProvider,
  DeleteAttachmentResolverRequest,
  DeleteCommentResolverRequest,
  GetCommentResolverRequest,
  ResolverConfig,
  SaveAttachmentResolverRequest,
  SaveCommentResolverRequest,
} from '@veltdev/types';
import { useCallback } from 'react';
import { store, RootState } from 'store';
import { useSelector } from 'react-redux';
import {
  useSaveCommentMutation,
  useDeleteCommentMutation,
  useSaveAttachmentMutation,
  useDeleteAttachmentMutation,
  notificationExtendedApiSlice,
} from 'store/slices/apiV1/notifications';
import { useGetUserQuery } from 'store/slices/apiV1/user';
import _ from 'lodash';

const resolverConfig: ResolverConfig = {
  resolveTimeout: 2000,
  getRetryConfig: {
    retryCount: 3,
    retryDelay: 2000,
  },
  saveRetryConfig: {
    retryCount: 3,
    retryDelay: 2000,
  },
  deleteRetryConfig: {
    retryCount: 3,
    retryDelay: 2000,
  },
};

export const useVeltDataProviders = () => {
  const [saveCommentMutation] = useSaveCommentMutation();
  const [deleteCommentMutation] = useDeleteCommentMutation();
  const [saveAttachmentMutation] = useSaveAttachmentMutation();
  const [deleteAttachmentMutation] = useDeleteAttachmentMutation();
  const orgId = useSelector((state: RootState) => state.org.activeOrgId);
  const { data: currentUser } = useGetUserQuery(undefined);

  const fetchCommentsFromDB = useCallback(
    async (request: GetCommentResolverRequest) => {
      try {
        const queryParams = {
          documentIds: request.documentIds,
          // Note: GetCommentResolverRequest only has documentIds, locationIds, folderIds
          // @ts-ignore - Dealing with Velt typing not being quite correct
          locationIds: request.locationIds, // @ts-ignore - Dealing with Velt typing not being quite correct
          folderIds: request.folderIds,
        };

        // Use the RTK Query endpoint directly by dispatching the query action
        const result = await store.dispatch(
          notificationExtendedApiSlice.endpoints.getCommentAnnotations.initiate(
            queryParams
          )
        );

        if (result.error) {
          return { success: false, statusCode: 500 };
        }

        // The API returns data in the Velt format, which matches the expected type
        const data = _.keyBy(
          // @ts-ignore - Dealing with Velt typing not being quite correct
          _.merge({}, ...(result.data?.data || [])),
          'annotationId'
        );

        return { data, success: true, statusCode: 200 };
      } catch (error) {
        return { success: false, statusCode: 500 };
      }
    },
    []
  );

  const saveCommentsToDB = useCallback(
    async (request: SaveCommentResolverRequest) => {
      try {
        // Transform Velt request to our API format
        const apiRequest = {
          user: `${orgId}_${currentUser?.id}`,
          comment_annotation: request.commentAnnotation,
        };

        // @ts-ignore - Dealing with Velt typing not being quite correct
        const result = await saveCommentMutation(apiRequest).unwrap();
        // Check for either created or updated IDs
        if ('createdCommentIds' in result || 'updatedCommentIds' in result) {
          return { success: true, statusCode: 200 };
        }
        return { success: false, statusCode: 500 };
      } catch (error) {
        return { success: false, statusCode: 500 };
      }
    },
    [saveCommentMutation, orgId, currentUser?.id]
  );

  const deleteCommentsFromDB = useCallback(
    async (request: DeleteCommentResolverRequest) => {
      try {
        // Transform Velt request to our API format
        // We need to construct the full annotation structure for deletion
        const apiRequest = {
          user: `${orgId}_${currentUser?.id}`,
          comment_annotation: {
            [request.commentAnnotationId]: {
              annotationId: request.commentAnnotationId,
              comments: {}, // Empty comments object for deletion
            },
          },
        };

        await deleteCommentMutation(apiRequest).unwrap();
        return { success: true, statusCode: 200 };
      } catch (error) {
        return { success: false, statusCode: 500 };
      }
    },
    [deleteCommentMutation, orgId, currentUser?.id]
  );

  const saveAttachmentsToDB = useCallback(
    async (request: SaveAttachmentResolverRequest) => {
      try {
        const formData = new FormData();

        // Append file if exists
        if (request.attachment?.file) {
          formData.append('file', request.attachment.file);
        }

        // Add attachment name if provided
        if (request.attachment?.name) {
          formData.append('name', request.attachment.name);
        }

        // Append metadata fields
        if (request.metadata) {
          if (request.metadata.documentId) {
            formData.append('document_id', request.metadata.documentId);
          }
          if (request.metadata.folderId) {
            formData.append('folder_id', request.metadata.folderId);
          }
          if (request.metadata.organizationId) {
            formData.append('org_id', request.metadata.organizationId);
          }
          if (request.metadata.commentAnnotationId) {
            formData.append(
              'comment_annotation_id',
              request.metadata.commentAnnotationId
            );
          }
        }

        await saveAttachmentMutation(formData).unwrap();
        return { success: true, statusCode: 200 };
      } catch (error) {
        return { success: false, statusCode: 500 };
      }
    },
    [saveAttachmentMutation]
  );

  const deleteAttachmentsFromDB = useCallback(
    async (request: DeleteAttachmentResolverRequest) => {
      try {
        // Transform Velt request to our API format
        const apiRequest = {
          id: String(request.attachmentId), // Convert number to string
        };

        await deleteAttachmentMutation(apiRequest).unwrap();
        return { success: true, statusCode: 200 };
      } catch (error) {
        return { success: false, statusCode: 500 };
      }
    },
    [deleteAttachmentMutation]
  );

  const commentAnnotationDataProvider: CommentAnnotationDataProvider = {
    get: fetchCommentsFromDB,
    save: saveCommentsToDB,
    delete: deleteCommentsFromDB,
    config: resolverConfig,
  };

  const attachmentDataProvider: AttachmentDataProvider = {
    save: saveAttachmentsToDB,
    delete: deleteAttachmentsFromDB,
    config: resolverConfig,
  };

  return {
    commentAnnotationDataProvider,
    attachmentDataProvider,
  };
};
